import type {HideableValue, RichTextContent, Values} from '@wix/devzai-utils-common';
import {hideableValueCreateHidden, immerProduce} from '@wix/devzai-utils-common';
import type {
    DoppeCountdownEndDateTime,
    DoppeCountdownSettings
} from '../../client-server-common/types/doppe-countdown-settings';
import {
    doppeActionTypeDefineMetadata
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {BymoPageMedia} from '../../client-server-common';
import {
    bymoPageMediaReplaceExternalImage,
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    BymoPagePropsSpecialContentSettingMetadata,
    bymoPagePropsCreateContentSettingMetadata
} from '../../client-server-common';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';

export const DoppeActionTypeCountdownStyle = {
    Minimalist: 'Minimalist',
    Tube: 'Tube',
    Dividers: 'Dividers',
    Stroke: 'Stroke',
    Blocks: 'Blocks',
} as const;

export type DoppeActionTypeCountdownStyle = Values<typeof DoppeActionTypeCountdownStyle>;

export namespace DoppeActionTypeCountdown {

    export interface ActionSettings extends
        DoppeCountdownSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings,
        DoppeActionMediaViewSettings {
        /**
         * Optional hero media rendered above the timer (image/video). `null` means no media.
         */
        media: BymoPageMedia[] | null;
        /**
         * Optional title rendered above the timer.
         */
        title: HideableValue<string>;
        /**
         * Optional rich-text description rendered above the timer.
         */
        description: HideableValue<RichTextContent>;
    }
}

const DOPPE_ACTION_TYPE_COUNTDOWN_DEFAULT_TARGET_TIME = '12:00';

function doppeActionTypeCountdownResolveDefaultTargetDate(): string {
    const future = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    return future.toISOString().slice(0, 10);
}

export function doppeCountdownResolveEndDateTime(
    settings: {
        endDateTime?: Partial<DoppeCountdownEndDateTime>;
        targetDate?: string;
        targetTime?: string;
        timeZoneId?: string;
    }
): DoppeCountdownEndDateTime {
    const nested = settings.endDateTime;
    const legacyDate = settings.targetDate;
    const legacyTime = settings.targetTime;
    const legacyZone = settings.timeZoneId;

    return {
        endDate: nested?.endDate || legacyDate || doppeActionTypeCountdownResolveDefaultTargetDate(),
        endTime: nested?.endTime ?? legacyTime ?? DOPPE_ACTION_TYPE_COUNTDOWN_DEFAULT_TARGET_TIME,
        timeZoneId: nested?.timeZoneId ?? legacyZone ?? 'UTC',
    };
}

export const doppeActionTypeCountdown = doppeActionTypeDefineMetadata<DoppeActionTypeCountdown.ActionSettings>({
    id: 'io.bymo.action/countdown',
    resolveActionSettings: action => {

        const {
            timerStyle = DoppeActionTypeCountdownStyle.Minimalist,
            media = null,
            title = hideableValueCreateHidden(''),
            description = hideableValueCreateHidden(''),
            hideWhenEnded = false,
            sections,
            ctaButtons,
        } = action.settings;

        return {
            endDateTime: doppeCountdownResolveEndDateTime(action.settings),
            timerStyle: timerStyle,
            media: media,
            ...doppeActionGetMediaViewDefaultSettingsMigration(action.settings),
            ...doppeActionContentSectionSettingsDefaultValuesMigration({sections: sections}),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration({ctaButtons: ctaButtons}, false),
            title: title,
            description: description,
            hideWhenEnded: hideWhenEnded,
        };
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            endDateTime: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
            timerStyle: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
            media: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleBymoPageMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            hideWhenEnded: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {},
        },
        supportsContentSections: {
            customContentSectionsTypes: {},
        },
        hideableContentSettingsNames: {
            title: true,
            description: true,
        },
        bindableContentSettingsNames: {},
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: false,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    resolveUsedMedia: actionSettings => {
        return actionSettings.media ?? [];
    },
    resolveMainMedia: actionSettings => {
        return actionSettings.media ? actionSettings.media[0] ?? null : null;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.media) {
                actionSettings.media = actionSettings.media.map(
                    image => bymoPageMediaReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
            }
        })
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps,
        }) ?? null;
    },
});
